// Centralized Play Store URL builder with UTM attribution.
//
// Why: every CTA pointing to Play Store should tag itself so the
// Play Console "Acquisition reports" can attribute installs to the
// specific button/page that drove them. Without this, all installs
// from the website show up as "(direct)" and you can't tell what's
// working.
//
// Usage:
//   import { playStoreUrl } from '@/utils/playStore';
//   <a href={playStoreUrl({ medium: 'landing_hero' })}>...</a>
//
// The Play Install Referrer API decodes the `referrer` query param
// and surfaces the UTMs in Play Console -> Acquisition reports ->
// "Tracking links" filter.

const APP_ID = 'com.wefaqna.wefaqna';

/**
 * Build a Play Store URL, optionally with UTM attribution.
 * @param {Object} [opts]
 * @param {string} [opts.source]   default 'website'
 * @param {string} [opts.medium]   e.g. 'footer', 'landing_hero', 'faq_page'
 * @param {string} [opts.campaign] defaults to medium if omitted
 * @param {string} [opts.inviteCode] an invitation to carry through the install
 * @returns {string}
 */
export function playStoreUrl({ source = 'website', medium, campaign, inviteCode } = {}) {
  const base = `https://play.google.com/store/apps/details?id=${APP_ID}`;
  if (!medium) return base;
  // `ref=` carries an invitation THROUGH the install: the app reads it from
  // the Play Install Referrer on first launch and sends it with the sign-up
  // (mobile_flutter/lib/core/services/invite_service.dart). Without it every
  // invitation sent to an Android phone was lost at the store.
  const refParts = `utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign || medium}`
    + (inviteCode ? `&ref=${encodeURIComponent(inviteCode)}` : '');
  return `${base}&referrer=${encodeURIComponent(refParts)}`;
}

// Google ships an official Play badge per language and forbids re-drawing it.
// The site had only the Arabic artwork, hardcoded at both download buttons, so
// English and French visitors were shown a badge they cannot read — alongside
// an Arabic `alt`, which a French screen reader then read out loud.
//
// A table keyed by language rather than a ternary, per INV-I18N-3: an unknown
// language falls back rather than rendering a broken image, and a new locale
// costs one line plus the asset.
//
// Only three badges exist. These are Google's own trademarked artwork with
// brand rules attached, so a missing language gets a badge we HAVE, never one
// we drew. What changed is WHICH one: the fallback used to be Arabic, so a
// German visitor — and now a Spanish one — was shown an Arabic badge on a
// Latin-script page, which is the exact complaint that put this table here in
// the first place. English is the honest stand-in until the real assets are
// downloaded from Google's badge generator.
const BADGE_BY_LANGUAGE = {
  ar: '/google-play-badge-ar.png',
  en: '/google-play-badge-en.png',
  fr: '/google-play-badge-fr.png',
};

//: Which badge a language we have no asset for should borrow.
const BADGE_FALLBACK_BY_SCRIPT = { ar: '/google-play-badge-ar.png' };

/**
 * Path to the official Google Play badge for the active UI language.
 * @param {string} [language] e.g. i18n.language ('fr', 'en-US', 'ar-EG')
 * @returns {string}
 */
export function playBadgeSrc(language) {
  const base = (language || 'ar').toLowerCase().split(/[-_]/)[0];
  return BADGE_BY_LANGUAGE[base]
    || BADGE_FALLBACK_BY_SCRIPT[base]
    || BADGE_BY_LANGUAGE.en;
}
